import React, { useEffect, useRef, useState } from 'react';

const GoogleMapComponent = ({ originAddress, destinationAddress, onRouteCalculated }) => {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);

  useEffect(() => {
    // Load Google Maps API if not already loaded
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places,geometry`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (mapLoaded && mapRef.current && originAddress && destinationAddress) {
      initializeMap();
    }
  }, [mapLoaded, originAddress, destinationAddress]);

  const initializeMap = () => {
    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 10,
      center: { lat: 32.7767, lng: -96.7970 }, // Dallas center as default
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      suppressMarkers: false,
      polylineOptions: {
        strokeColor: '#4285F4',
        strokeWeight: 4,
        strokeOpacity: 0.8,
      },
    });

    directionsRenderer.setMap(map);

    // Calculate route
    directionsService.route(
      {
        origin: originAddress,
        destination: destinationAddress,
        travelMode: window.google.maps.TravelMode.DRIVING,
        avoidHighways: false,
        avoidTolls: false,
      },
      (response, status) => {
        if (status === 'OK') {
          directionsRenderer.setDirections(response);
          
          const route = response.routes[0];
          const leg = route.legs[0];
          
          const calculatedRouteInfo = {
            distance: leg.distance.text,
            duration: leg.duration.text,
            startAddress: leg.start_address,
            endAddress: leg.end_address,
            steps: leg.steps.map(step => ({
              instructions: step.instructions,
              distance: step.distance.text,
              duration: step.duration.text,
            })),
          };

          setRouteInfo(calculatedRouteInfo);
          
          // Pass route info back to parent component
          if (onRouteCalculated) {
            onRouteCalculated(calculatedRouteInfo);
          }
        } else {
          console.error('Directions request failed due to ' + status);
          // Fallback to mock data
          setRouteInfo({
            distance: '12.5 miles',
            duration: '18 minutes',
            startAddress: originAddress,
            endAddress: destinationAddress,
            error: 'Could not calculate route. Using estimated values.',
          });
        }
      }
    );
  };

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="text-yellow-600">⚠️</div>
          <h3 className="font-semibold text-yellow-800">Google Maps API Key Required</h3>
        </div>
        <p className="text-yellow-700 mb-4">
          To display real Google Maps with route calculation, you need to:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-yellow-700 mb-4">
          <li>Get a Google Maps API key from Google Cloud Console</li>
          <li>Enable Maps JavaScript API and Directions API</li>
          <li>Add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file</li>
          <li>Restart the application</li>
        </ol>
        <div className="bg-white rounded border p-4">
          <p className="text-sm text-gray-600 mb-2">Mock Route Information:</p>
          <p className="font-medium">From: {originAddress}</p>
          <p className="font-medium">To: {destinationAddress}</p>
          <p className="text-blue-600">Distance: 12.5 miles • Duration: 18 minutes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        ref={mapRef} 
        style={{ width: '100%', height: '400px' }}
        className="rounded-lg border border-gray-200"
      />
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-gray-600">Loading Google Maps...</p>
          </div>
        </div>
      )}
      {routeInfo && routeInfo.error && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-700 text-sm">{routeInfo.error}</p>
        </div>
      )}
    </div>
  );
};

export default GoogleMapComponent;